import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DP_PREVIEW_MAX_POLL_ATTEMPTS,
  DP_PREVIEW_POLL_FAILED_MESSAGE,
  DP_PREVIEW_POLL_TIMEOUT_MESSAGE,
  type DpPreviewInitialPayload,
  fetchDpPreviewStatus,
  isAbortError,
  isGeneratingDpPreviewStatus,
  submitDpPreview
} from '../api'
import { digitalProofingKeys } from '../keys'
import { dpPreviewStatusQueryOptions } from '../queries'

export function useDigitalProofPreview() {
  const queryClient = useQueryClient()
  const abortRef = useRef<AbortController | null>(null)
  const pollAttemptRef = useRef(0)

  const [asyncProductId, setAsyncProductId] = useState<number | null>(null)
  const [syncPreview, setSyncPreview] = useState<DpPreviewInitialPayload | null>(null)
  const [initialAsyncPreview, setInitialAsyncPreview] = useState<DpPreviewInitialPayload | null>(null)

  const clearPreviewState = useCallback(
    (productIdToRemove?: number | null) => {
      abortRef.current?.abort()
      abortRef.current = null
      pollAttemptRef.current = 0
      setAsyncProductId(null)
      setSyncPreview(null)
      setInitialAsyncPreview(null)

      if (typeof productIdToRemove === 'number') {
        void queryClient.cancelQueries({
          queryKey: digitalProofingKeys.previewStatus.byProductId(productIdToRemove),
          exact: true
        })
      }
    },
    [queryClient]
  )

  const mutation = useMutation({
    mutationFn: (formData: FormData) => submitDpPreview(formData, abortRef.current?.signal),
    retry: false,
    onSuccess: result => {
      if (result.type === 'sync') {
        setSyncPreview(result.preview)
      } else if (result.type === 'async') {
        setInitialAsyncPreview(result.initial)
        setAsyncProductId(result.productId)
      }
    }
  })

  useEffect(() => {
    pollAttemptRef.current = 0
  }, [asyncProductId])

  const statusQuery = useQuery({
    ...dpPreviewStatusQueryOptions(asyncProductId ?? 0),
    queryFn: async ({ signal }) => {
      if (asyncProductId === null) throw new Error('Cannot poll a proof preview without a product ID.')

      const data = await fetchDpPreviewStatus(asyncProductId, signal)
      pollAttemptRef.current += 1

      if (data.proof_status !== 'ready' && data.proof_status !== 'failed') {
        if (pollAttemptRef.current >= DP_PREVIEW_MAX_POLL_ATTEMPTS) {
          throw new Error(DP_PREVIEW_POLL_TIMEOUT_MESSAGE)
        }
      }

      return data
    },
    enabled: asyncProductId !== null
  })

  const proofingPreview = useMemo((): DpPreviewInitialPayload | null => {
    if (syncPreview) return syncPreview

    if (asyncProductId !== null && initialAsyncPreview) {
      const statusData = statusQuery.data

      if (statusData?.proof_status === 'ready') {
        return {
          ...initialAsyncPreview,
          proof_status: statusData.proof_status,
          preview: statusData.preview
        }
      }

      if (statusData) {
        return { ...initialAsyncPreview, proof_status: statusData.proof_status }
      }

      return initialAsyncPreview
    }

    return null
  }, [syncPreview, asyncProductId, initialAsyncPreview, statusQuery.data])

  const isSubmitting = mutation.isPending

  const error = useMemo(() => {
    if (mutation.error && !isAbortError(mutation.error)) return mutation.error
    if (statusQuery.error && !isAbortError(statusQuery.error)) return statusQuery.error
    if (statusQuery.data?.proof_status === 'failed') return new Error(DP_PREVIEW_POLL_FAILED_MESSAGE)
    return null
  }, [mutation.error, statusQuery.data?.proof_status, statusQuery.error])

  const isFailed = error !== null

  const isGenerating =
    !isFailed &&
    (isSubmitting ||
      (asyncProductId !== null && (!statusQuery.data || isGeneratingDpPreviewStatus(statusQuery.data.proof_status))) ||
      isGeneratingDpPreviewStatus(proofingPreview?.proof_status))

  const submitPreview = useCallback(
    (formData: FormData) => {
      const previousProductId = asyncProductId
      clearPreviewState(previousProductId)
      mutation.reset()
      abortRef.current = new AbortController()
      mutation.mutate(formData)
    },
    [asyncProductId, clearPreviewState, mutation]
  )

  const resetPreview = useCallback(() => {
    clearPreviewState(asyncProductId)
    mutation.reset()
  }, [asyncProductId, clearPreviewState, mutation])

  const cancelPreview = resetPreview

  return {
    proofingPreview,
    submitPreview,
    resetPreview,
    cancelPreview,
    isSubmitting,
    isGenerating,
    isFailed,
    error
  }
}
